import { t } from '@avalara/bat-apps-shared/i18n';
import conf from '../../conf';
import { setSystemError } from '../../store/system';
import { submitPublicSupportCaseTextAndLink, submitSupportCaseTextAndLink } from '../constants';
import { emailExistsMessage, isInvalidAddressError, matchesError } from '../errors';
import { checkIsAuthenticated, embeddedViewRequested, getAuthLogin } from '../helpers';

import type { Dispatch } from '@avalara/types/actions';
import type { ActiveFlow } from '../../../../types';

const contactUsUrl = 'https://www.avalara.com/us/en/about/contact-us.html';
const embeddedAProps = embeddedViewRequested() ? ' target="_blank" rel="noopener noreferrer"' : '';

type GetSystemErrorMessagingProps = {
  activeFlow?: ActiveFlow;
  errorCode: string;
  isPaid?: boolean;
  locationString?: string;
  msg?: string;
  offerFlow?: string | null;
  offerName?: string;
  partnerName?: string;
  productName?: string;
  returnsEligibilityStatus?: string;
  serverMsg?: string;
};

export const getSystemErrorMessaging =
  (errorData: GetSystemErrorMessagingProps) =>
  async (dispatch: Dispatch): Promise<void> => {
    const {
      errorCode,
      isPaid,
      locationString,
      msg = '',
      offerName,
      partnerName = '',
      productName = '',
      returnsEligibilityStatus,
      serverMsg,
    } = errorData;

    let displaySimpleMessage = false;
    let html: string | undefined;
    let locationAfterErrorDismissal = locationString;
    let message = msg;
    let secondaryMessage = [] as Array<string>;
    let title = '';

    switch (errorCode) {
      case 'accountAlreadyExists':
        title = t('client.errors.account-exists-title');
        html = t('client.errors.account-exists-message', {
          signInLinkAndText: `<a href="${conf.CLIENT_URL}/auth/login?returnUrl=https%3A%2F%2Fbuy.avalara.com%2F">Sign in</a>`,
          submitPublicSupportCaseTextAndLink,
        });
        break;
      case 'defaultIneligibility':
        // Removing additional whitespace in case offername is empty
        title = t('client.errors.default-ineligibility-title', { offerName }).replace(/\s{2,}/g, ' ');
        html = t('client.errors.default-ineligibility-html', {
          accountLink: `<a href="${conf.AA_URL}"${embeddedAProps}>${t('client.errors.default-ineligibility-account-link')}</a>`,
          contactLink: `<a href="${contactUsUrl}"${embeddedAProps}>${t('client.errors.default-ineligibility-contact-link')}</a>`,
        });
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'emailAlreadyExists':
        title = t('client.errors.account-exists-title');
        html = emailExistsMessage(productName);
        break;
      case 'existingPaidAvatax':
        title = t('client.errors.existing-paid-avatax-title');
        message = t('client.errors.existing-paid-avatax-message');
        secondaryMessage = [t('client.errors.contact-upgrade-prefix'), t('client.errors.contact-upgrade-phone')];
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'failedAccountFetch':
        title = t('client.errors.generic-title');
        message = t('client.errors.failed-account-fetch-message');
        secondaryMessage = [t('client.errors.try-again-contact-prefix'), t('client.errors.contact-upgrade-phone')];
        locationAfterErrorDismissal = `${conf.AA_URL}/auth/login`;
        break;
      case 'failedCybersourcePayment':
        title = t('client.errors.failed-cybersource-payment-title');
        message = msg;
        displaySimpleMessage = true;
        break;
      case 'failedReturnsAccountFetch':
        title = t('client.errors.generic-title');
        message = t('client.errors.must-be-logged-in-message');
        secondaryMessage = [t('client.errors.try-again-contact-prefix'), t('client.errors.contact-upgrade-phone')];
        locationAfterErrorDismissal = getAuthLogin();
        break;
      case 'failedUsageFetch':
        title = t('client.errors.failed-usage-fetch-title');
        message = t('client.errors.failed-usage-fetch-message');
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'ineligibleConnector':
        title = t('client.errors.ineligible-connector-title');
        message = t('client.errors.ineligible-connector-message');
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'ineligibleForReturnsPurchase':
        title = t('client.errors.ineligible-returns-title');
        secondaryMessage = [t('client.errors.contact-upgrade-prefix'), t('client.errors.contact-upgrade-phone')];
        // TODO (Andrew) - confirm where we want to send users in case of this error
        locationAfterErrorDismissal = conf.AA_URL;
        message = (() => {
          switch (returnsEligibilityStatus) {
            case 'NO_PERMANENT_AVATAX':
              return t('client.errors.ineligible-returns-no-permanent-avatax');
            case 'ALREADY_HAS_RETURNS':
            case 'ALREADY_HAS_BASIC_RETURNS':
              return t('client.errors.ineligible-returns-already-has-returns');
            default:
              return '';
          }
        })();
        break;
      case 'missingConnector':
        title = t('client.errors.missing-connector-title');
        message = t('client.errors.missing-connector-message');
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'noBillingContact':
        title = t('client.errors.generic-title');
        message = t('client.errors.no-billing-contact-message');
        secondaryMessage = [t('client.errors.try-again-contact-prefix'), t('client.errors.contact-upgrade-phone')];
        locationAfterErrorDismissal = `${conf.CLIENT_URL}/auth/logout`;
        break;
      case 'notAvailableForExistingAccounts':
        title = t('client.errors.not-available-existing-accounts-title');
        html = t('client.errors.not-available-existing-accounts-html', {
          accountManagerLink: `<a href="${conf.AA_URL}"${embeddedAProps}>${t('client.errors.contact-your-account-manager')}</a>`,
          offerName: offerName || t('client.errors.this-product'),
        });
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'prequalQuestionnaireError':
        title = t('client.errors.prequal-questionnaire-title');
        message = t('client.errors.prequal-questionnaire-message');
        locationAfterErrorDismissal = 'https://www.avalara.com/us/en/get-started.html';
        break;
      case 'redirectOutOfSandbox':
        title = t('client.errors.redirect-out-of-sandbox-title');
        message = t('client.errors.redirect-out-of-sandbox-message');
        locationAfterErrorDismissal = `${conf.CLIENT_URL}${getAuthLogin()}`;
        break;
      case 'signupError': {
        if (isInvalidAddressError(serverMsg || '')) {
          title = t('client.errors.invalid-address-title');
          message = t('client.errors.invalid-address-message');
          break;
        }
        const supportTicketLink = checkIsAuthenticated()
          ? submitSupportCaseTextAndLink
          : submitPublicSupportCaseTextAndLink;
        title = t('client.errors.generic-title');
        message = `${msg}${isPaid ? t('client.errors.signup-error-payment-suffix') : ''}`;
        html = t('client.errors.signup-error-html', { supportTicketLink });
        locationAfterErrorDismissal = matchesError(serverMsg || '', 'client.errors.matcher.email-already-exists')
          ? conf.AA_URL
          : '';
        break;
      }
      case 'upsStoreReturnsIneligible':
        title = t('client.errors.not-available-existing-accounts-title');
        html = t('client.errors.ups-store-returns-ineligible-html', {
          managedReturnsLink: `<a href="${conf.CUP_URL}/returns/upgrade">${t('client.errors.get-avalara-managed-returns')}</a>`,
          supportCaseLink: `<a href="${conf.AA_URL}/#/support-cases">${t('client.errors.submit-a-support-case')}</a>`,
        });
        locationAfterErrorDismissal = conf.AA_URL;
        break;
      case 'unsupportedConnector':
        title = t('client.errors.generic-title');
        message = t('client.errors.unsupported-connector-message');
        secondaryMessage = [t('client.errors.please-contact-us-at'), t('client.errors.contact-upgrade-phone')];
        locationAfterErrorDismissal = '/';
        break;
      case 'esbReturnUrlMissing':
        title = t('client.errors.esb-return-url-missing-title', { partnerName });
        message = t('client.errors.esb-return-url-missing-message', { partnerName });
        locationAfterErrorDismissal = '/';
        break;
      case 'ips-api-error':
        title = t('client.errors.ips-api-error-title');
        message = t('client.errors.ips-api-error-message');
        break;
      case 'ineligibleForAccountsPayable':
        title = t('client.errors.generic-title');
        message = t('client.errors.must-be-logged-in-message');
        secondaryMessage = [
          t('client.errors.please-log-in-or-contact-us-at'),
          t('client.errors.contact-upgrade-phone'),
        ];
        locationAfterErrorDismissal = `${conf.CLIENT_URL}${getAuthLogin()}`;
        break;
      case 'chiliPiperBookingError':
        title = t('client.errors.generic-title');
        message = t('client.errors.chilipiper-booking-message');
        secondaryMessage = [t('client.errors.try-again-contact-prefix'), t('client.errors.contact-upgrade-phone')];
        break;
      // case 'genericError': same as default
      default:
        title = t('client.errors.generic-title');
        html = t('client.errors.generic-html', {
          contactLink: `<a href="${contactUsUrl}"${embeddedAProps}>${t('client.errors.default-ineligibility-contact-link')}</a>`,
        });
        break;
    }

    dispatch(
      setSystemError({
        message,
        secondaryMessage,
        title,
        html,
        serverMessage: serverMsg,
        locationAfterErrorDismissal,
        displaySimpleMessage,
      }),
    );
  };
